import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  deleteImage,
  uploadImageForGallery,
  uploadImageForProfile,
} from "api/image.api.ts";
import { updateProfileInformation } from "api/teacher.api.ts";
import { useAuth } from "context/AuthContext.jsx";
import {
  teacherLocalKey,
  teacherStepLocalKey,
  useTeacherCreation,
} from "context/TeacherCreationContext.jsx";
import { useToast } from "hooks/index.js";
import { useSelectImages } from "hooks/useSelectImages.js";
import { appRoute } from "pages/app/app.routes";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { HelperText } from "ui/base/helperText/HelperText.jsx";

import { ButtonPrimary } from "ui/base/Button/ButtonStyled";
import { DeleteIcon } from "ui/icons/index.js";
import { ImageIcon } from "ui/icons/inputIcons/ImageIcon.jsx";
import {convertHexToRGBA, defaultThemeColors, mediaSizes, themeColor} from "ui/theme";
import { pixelTrackEvent } from "utils/facebookPixel.js";
import { safeLocalStorage } from "utils/localStorage.ts";
import { InputSignUp } from "ui/base/InputSignUp/InputSignUp";
import YouTube from "react-youtube";
import { extractYouTubeVideoId } from "utils/utils.ts";
import {PhotoGalleryUploader} from "../../../ui/base/photoGalleryUploader/PhotoGalleryUploader";
import { LoadingSpinner } from "ui/base/loading/LoadingSpinner";

export const PhotosPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const {
    formData,
    changeFormData: changeTeacherInfo,
    setFormData,
  } = useTeacherCreation();

  const [loading, setLoading] = useState(false);

  const [formDataErrors, setFormDataErrors] = useState({});

  const { selectedImages, setSelectedImages, handleImageDeselect } =
    useSelectImages(userData?.gallery || []);

  const { mutate: submitProfile } = useMutation({
    mutationFn: updateProfileInformation,
    onError: (err) => {
      setLoading(false);
      useToast("error", "submitProfile error");
    },
    onSuccess: (data) => {
      setLoading(false);
      queryClient.invalidateQueries({ queryKey: ["userDetail"] });
      navigate(appRoute.completeProfileFifth.fullPath);
    },
  });

  const { mutateAsync: startUploadingImages } = useMutation({
    mutationFn: uploadImageForGallery,
  });

  const addImagesToGallery = async (uploadedProfileImage) => {
    try {
      let newGallery = [];
      for (const photoInfo of selectedImages) {
        if (photoInfo?.deleted) {
          continue;
        }
        if (photoInfo?.uploaded) {
          newGallery.push(photoInfo);
          continue;
        }
        const res = await startUploadingImages(photoInfo);
        newGallery.push({
          ...photoInfo,
          name: "",
          type: "",
          uri: res,
          uploaded: true,
          deleted: false,
        });
      }

      setFormData((prevState) => ({
        ...prevState,
        gallery: newGallery,
      }));

      const mappedGallery = newGallery?.map((it) => {
        const idx = it.uri.lastIndexOf("/");
        return {
          name: it.uri.slice(idx + 1),
        };
      });

      submitProfile({
        ...formData,
        price_per_group_class: formData?.price_per_group_class || 0,
        price_per_private_class: formData?.price_per_private_class || 0,
        price_per_online_class: formData?.price_per_online_class || 0,
        number_of_students: formData?.number_of_students || 0,
        number_of_students_private_class:
          formData?.number_of_students_private_class || 0,
        gallery: mappedGallery,
        profile_completion: "50",
        new_signup: formData.profile_completion === "" ? true : false,
        profile_pic: uploadedProfileImage,
      });
    } catch (error) {
      setLoading(false);
      console.error(error);
      useToast("error", "Upload image error");
    }
  };

  const { mutate: deleteUploadedImages } = useMutation({mutationFn:deleteImage});

  const deleteUploadedImagesFromGallery = () => {
    for (const photoInfo of selectedImages) {
      if (photoInfo?.deleted && photoInfo?.uploaded) {
        deleteUploadedImages(photoInfo, "gallery");
      }
    }
  };

  const { mutate: onUploadImage } = useMutation({
    mutationFn: uploadImageForProfile,
    onError: (err) => {
      setLoading(false);
      console.error("onUploadImage", err);
    },
    onSuccess: (data) => {
      const idx1 = data?.lastIndexOf("/");
      const uploadUri = data.slice(idx1 + 1);
      changeTeacherInfo("profile_pic", {
        ...formData.profile_pic,
        uri: data,
      });
      addImagesToGallery(uploadUri);
      deleteUploadedImagesFromGallery();
    },
  });

  const onSubmit = () => {
    let submitErrors = {};
    // Validate image count
    const undeletedImages = selectedImages?.filter((img) => !img.deleted);

    if (undeletedImages.length < 2) {
      submitErrors = { ...submitErrors, gallery: "Select at least 2 images" };

      setFormDataErrors((prevState) => ({
        ...prevState,
        gallery: "Select at least 2 images",
      }));
    } else {
      submitErrors = { ...submitErrors, gallery: "" };
      setFormDataErrors((prevState) => ({
        ...prevState,
        gallery: "",
      }));
    }

    // Check for any errors
    const hasErrors = Object.values(submitErrors).some((error) => error !== "");

    if (hasErrors) {
      useToast("error", "Add at least 2 images");
      return;
    }

    setLoading(true);
    safeLocalStorage.setItem(teacherLocalKey, formData);
    safeLocalStorage.setItem(teacherStepLocalKey, 4);
    onUploadImage({ image: formData.profile_pic, userType: "instructor" });
  };

  useEffect(() => {
    pixelTrackEvent("Enter Instructor Sign-up Page 4");
  }, []);

  const youtubeId = extractYouTubeVideoId(formData.intro_video_url);

  return (
    <Container>
      <InnerContainer>
        <HelperText style={{
          marginTop: "-1rem",
          marginBottom: "-1rem"
        }}>
          Add a video link introducing yourself and telling the users about your class.
          <PurpleText>{" (Optional)"}</PurpleText> <br />In order to share a video, you would have to upload it to YouTube and paste the link below
        </HelperText>
        <InputSignUp
          value={formData.intro_video_url}
          setValue={(value) => {
            changeTeacherInfo("intro_video_url", value);
          }}
        />
        {youtubeId ? (
          <VideoWrapper>
            <YouTube
              className="youtube-wrapper desktop"
              iframeClassName="youtube-iframe"
              videoId={extractYouTubeVideoId(formData.intro_video_url)}
            />
          </VideoWrapper>
        ) : null}
        <div>
            <PhotoGalleryUploader
                selectedImages={selectedImages}
                setSelectedImages={setSelectedImages}
                handleImageDeselect={handleImageDeselect}
                formDataErrors={formDataErrors}
            />
        </div>

        <div style={{ width: "100%", display: "flex" }}>
          <ButtonPrimary
            onClick={() => {
              pixelTrackEvent(
                "Instructor Sign-up Page 4: Clicked on Next step"
              );
              onSubmit();
            }}
            disabled={loading || selectedImages?.length < 2}
          >
            {loading ? (
              <>
                <LoadingSpinner variant="white" />
                <span
                  style={{
                    color: "#d9d9d9",
                  }}
                >
                  Loading...
                </span>
              </>
            ) : (
              "Next Step"
            )}
          </ButtonPrimary>
        </div>
      </InnerContainer>
    </Container>
  );
};

const Container = styled.div`
  display: grid;
  gap: 1rem;
  padding-top: 2rem;
  font-family: var(--font-secondary);
`;

const InnerContainer = styled.div`
  display: grid;
  gap: 40px;
  grid-column: span 2;
`;

const GalleryContainer = styled.div`
  display: grid;
  gap: 1rem;
`;

const GalleryAddButton = styled.label`
  padding: 20px 38px;
  background-color: ${themeColor("border")};
  border-width: 2px;
  border-style: dashed;
  border-color: ${convertHexToRGBA(defaultThemeColors.blue, 0.5)};
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 5px;
  cursor: pointer;
  height: 140px;
  @media (min-width: 768px) {
    height: 280px;
  }
`;

const GalleryImagesContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;

    @media (min-width: ${mediaSizes.md}) {
        grid-template-columns: repeat(4, 1fr);
    }

    @media (min-width: ${mediaSizes.xl}) {
        grid-template-columns: repeat(8, 1fr);
    }
`;

const GalleryAddButtonTitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 1rem;
`;

const GalleryImageContainer = styled.div`
    position: relative;
`;

const MainImage = styled.div`
    background-color: ${defaultThemeColors.pink};
    color: ${defaultThemeColors.white};
    padding-inline: 0.5rem;
    padding-block: 0.625rem;
    border-bottom-right-radius: 1rem;
    border-bottom-left-radius: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
    white-space: nowrap;
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 90px;
  object-fit: cover;
  object-position: center;
  border-radius: 10px;
  border-width: 1px;
  border-style: solid;
  border-color: ${convertHexToRGBA(defaultThemeColors.blue, 0.5)};
`;

const GalleryDeleteImageContainer = styled.div`
  position: absolute;
  background-color: ${defaultThemeColors.pink};
  color: ${defaultThemeColors.white};
  right: 0;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  cursor: pointer;
  border-top-right-radius: 10px;
  border-bottom-left-radius: 5px;
`;

const VideoWrapper = styled.div`
  overflow: hidden;
  max-width: 300px;
  margin-inline: auto;

  & > div {
    aspect-ratio: 16/9;
    overflow: hidden;
    border-radius: 5px;

    & .youtube-wrapper,
    .youtube-iframe {
      width: 100%;
      height: 100%;
    }
  }
`;

const PurpleText = styled.p`
  display: inline;
  color: ${themeColor("blue")};
`;
